import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent } from '../ui/Card';
import { ArrowUpRight, ArrowDownRight, Info, BoxIcon } from 'lucide-react';
import { cn } from '../../utils/cn';
import { AnimatePresence, motion } from 'framer-motion';
interface StatCardProps {
  title: string;
  value: string | number;
  icon: BoxIcon;
  trend?: {
    value: number | string;
    isPositive: boolean;
    label?: string;
  };
  className?: string;
  iconColor?: string;
  description?: string;
  percentOfTotal?: string;
}
export function StatCard({
  title,
  value,
  icon: Icon,
  trend,
  className,
  iconColor = 'text-primary-600',
  description,
  percentOfTotal
}: StatCardProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!showTooltip) return;
    function handleClickOutside(e: MouseEvent) {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node) && buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
        setShowTooltip(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTooltip]);
  return <Card className={cn('relative', className)} data-id="element-78">
      <CardContent className="p-6" data-id="element-79">
        <div className="flex items-center justify-between" data-id="element-80">
          <div className="flex-1" data-id="element-81">
            <div className="flex items-center gap-1.5 relative" data-id="element-82">
              <p className="text-sm font-medium text-slate-500" data-id="element-83">{title}</p>
              {description && <>
                  <button ref={buttonRef} onClick={() => setShowTooltip(!showTooltip)} className="text-slate-400 hover:text-primary-600 transition-colors flex-shrink-0" aria-label={`Info about ${title}`} data-id="element-84">
                    <Info className="w-3.5 h-3.5" data-id="element-85" />
                  </button>
                  <AnimatePresence data-id="element-86">
                    {showTooltip && <motion.div ref={tooltipRef} initial={{
                  opacity: 0,
                  y: 4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 4
                }} transition={{
                  duration: 0.15
                }} className="absolute left-0 top-full mt-2 z-50 w-64 bg-slate-900 text-white text-xs rounded-lg p-3 shadow-lg border border-slate-700" data-id="element-87">
                        <div className="absolute -top-1.5 left-4 w-3 h-3 bg-slate-900 border-l border-t border-slate-700 rotate-45" data-id="element-88" />
                        <p className="leading-relaxed" data-id="element-89">{description}</p>
                      </motion.div>}
                  </AnimatePresence>
                </>}
            </div>
            <div className="mt-2 flex items-baseline gap-2 flex-wrap" data-id="element-90">
              <span className="text-2xl font-bold text-slate-900" data-id="element-91">{value}</span>
              {percentOfTotal && <span className="text-xs font-medium text-slate-400" data-id="element-92">
                  ({percentOfTotal})
                </span>}
              {trend && <span className={cn('flex items-center text-xs font-medium', trend.isPositive ? 'text-primary-600' : 'text-red-600')} data-id="element-93">
                  {trend.isPositive ? <ArrowUpRight className="mr-1 h-3 w-3" data-id="element-94" /> : <ArrowDownRight className="mr-1 h-3 w-3" data-id="element-95" />}
                  {trend.value}
                  {trend.label && <span className="ml-1 text-slate-400 font-normal" data-id="element-96">
                      {trend.label}
                    </span>}
                </span>}
            </div>
          </div>
          <div className={cn('flex h-12 w-12 items-center justify-center rounded-lg bg-slate-50 flex-shrink-0 ml-2', iconColor)} data-id="element-97">
            <Icon className="h-6 w-6" data-id="element-98" />
          </div>
        </div>
      </CardContent>
    </Card>;
}